/**
 * Guest Wishlist Manager
 * Manages wishlist persistence for non-logged-in users using browser localStorage
 * Automatically syncs with user account when they log in
 */

class GuestWishlistManager {
  constructor() {
    this.STORAGE_KEY = 'urban_district_guest_wishlist';
    this.EXPIRATION_DAYS = 30;
    this.MS_PER_DAY = 86400000;
    this.init();
  }

  /**
   * Initialize the guest wishlist manager
   */
  init() {
    this.ensureSessionValidation();
    this.setupLoginObserver();
  }

  /**
   * Get all wishlists for guest user
   * @returns {Array} Array of wishlist objects
   */
  getAllWishlists() {
    const data = this.getStorageData();
    return data?.wishlists || [];
  }

  /**
   * Get a specific wishlist by ID
   * @param {string} wishlistId - The ID of the wishlist
   * @returns {Object|null} Wishlist object or null if not found
   */
  getWishlist(wishlistId) {
    const wishlists = this.getAllWishlists();
    return wishlists.find(w => w.id === wishlistId) || null;
  }

  /**
   * Create a new wishlist
   * @param {string} name - Name of the wishlist
   * @returns {Object} New wishlist object
   */
  createWishlist(name = 'Default Wishlist') {
    const wishlists = this.getAllWishlists();
    const newWishlist = {
      id: `wishlist_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: name,
      items: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isGuest: true
    };

    wishlists.push(newWishlist);
    this.saveWishlists(wishlists);
    this.broadcastChange('wishlist_created', newWishlist);

    return newWishlist;
  }

  /**
   * Add item to wishlist
   * @param {string} wishlistId - The ID of the wishlist
   * @param {Object} item - Item to add {productId, variantId, title, price, image, handle}
   * @returns {Object} Updated wishlist
   */
  addItemToWishlist(wishlistId, item) {
    const wishlists = this.getAllWishlists();
    const wishlist = wishlists.find(w => w.id === wishlistId);

    if (!wishlist) {
      console.warn(`Wishlist ${wishlistId} not found`);
      return null;
    }

    // Prevent duplicates
    const itemKey = `${item.productId}_${item.variantId}`;
    const existingItem = wishlist.items.find(
      i => `${i.productId}_${i.variantId}` === itemKey
    );

    if (!existingItem) {
      const newItem = {
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        ...item,
        addedAt: new Date().toISOString()
      };

      wishlist.items.push(newItem);
      wishlist.updatedAt = new Date().toISOString();
      this.saveWishlists(wishlists);
      this.broadcastChange('item_added', { wishlist, item: newItem });
    }

    return wishlist;
  }

  /**
   * Remove item from wishlist
   * @param {string} wishlistId - The ID of the wishlist
   * @param {string} itemId - The ID of the item to remove
   * @returns {Object} Updated wishlist
   */
  removeItemFromWishlist(wishlistId, itemId) {
    const wishlists = this.getAllWishlists();
    const wishlist = wishlists.find(w => w.id === wishlistId);

    if (!wishlist) {
      console.warn(`Wishlist ${wishlistId} not found`);
      return null;
    }

    const initialLength = wishlist.items.length;
    wishlist.items = wishlist.items.filter(i => i.id !== itemId);

    if (wishlist.items.length < initialLength) {
      wishlist.updatedAt = new Date().toISOString();
      this.saveWishlists(wishlists);
      this.broadcastChange('item_removed', { wishlist, itemId });
    }

    return wishlist;
  }

  /**
   * Delete entire wishlist
   * @param {string} wishlistId - The ID of the wishlist
   * @returns {boolean} Success status
   */
  deleteWishlist(wishlistId) {
    const wishlists = this.getAllWishlists();
    const filteredWishlists = wishlists.filter(w => w.id !== wishlistId);

    if (filteredWishlists.length < wishlists.length) {
      this.saveWishlists(filteredWishlists);
      this.broadcastChange('wishlist_deleted', { wishlistId });
      return true;
    }

    return false;
  }

  /**
   * Update wishlist name
   * @param {string} wishlistId - The ID of the wishlist
   * @param {string} newName - New name for the wishlist
   * @returns {Object} Updated wishlist
   */
  updateWishlistName(wishlistId, newName) {
    const wishlists = this.getAllWishlists();
    const wishlist = wishlists.find(w => w.id === wishlistId);

    if (wishlist) {
      wishlist.name = newName;
      wishlist.updatedAt = new Date().toISOString();
      this.saveWishlists(wishlists);
      this.broadcastChange('wishlist_updated', wishlist);
    }

    return wishlist || null;
  }

  /**
   * Check if item exists in any wishlist
   * @param {string|number} productId - Product ID to check
   * @param {string|number} variantId - Variant ID to check
   * @returns {boolean} True if item exists in any wishlist
   */
  isItemInWishlist(productId, variantId) {
    const wishlists = this.getAllWishlists();
    return wishlists.some(w =>
      w.items.some(i => i.productId == productId && i.variantId == variantId)
    );
  }

  /**
   * Get all wishlists that contain a specific item
   * @param {string|number} productId - Product ID
   * @param {string|number} variantId - Variant ID
   * @returns {Array} Array of wishlists containing the item
   */
  getWishlistsContainingItem(productId, variantId) {
    const wishlists = this.getAllWishlists();
    return wishlists.filter(w =>
      w.items.some(i => i.productId == productId && i.variantId == variantId)
    );
  }

  /**
   * Export wishlist data (for sharing or migration)
   * @param {string} wishlistId - The ID of the wishlist to export
   * @returns {string} JSON string of wishlist data
   */
  exportWishlist(wishlistId) {
    const wishlist = this.getWishlist(wishlistId);
    if (!wishlist) return null;

    return JSON.stringify({
      ...wishlist,
      exportedAt: new Date().toISOString()
    });
  }

  /**
   * Clear all guest wishlists
   */
  clearAllWishlists() {
    localStorage.removeItem(this.STORAGE_KEY);
    this.broadcastChange('wishlists_cleared');
  }

  /**
   * Get storage data with expiration check
   * @returns {Object|null} Stored data or null if expired
   */
  getStorageData() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);

      // Check if data has expired
      if (this.isExpired(data.createdAt)) {
        localStorage.removeItem(this.STORAGE_KEY);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error reading wishlist storage:', error);
      return null;
    }
  }

  /**
   * Save wishlists to storage
   * @param {Array} wishlists - Array of wishlist objects
   */
  saveWishlists(wishlists) {
    try {
      const existingData = this.getStorageData();
      const data = {
        wishlists,
        createdAt: existingData?.createdAt || new Date().toISOString(),
        version: 1
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('Error saving wishlists:', error);
      // Fallback: Clear storage and try again
      try {
        localStorage.removeItem(this.STORAGE_KEY);
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } catch (fallbackError) {
        console.error('Failed to save wishlists even after clearing:', fallbackError);
      }
    }
  }

  /**
   * Check if data has expired
   * @param {string} createdAt - ISO timestamp of when data was created
   * @returns {boolean} True if expired
   */
  isExpired(createdAt) {
    const created = new Date(createdAt).getTime();
    const now = new Date().getTime();
    return (now - created) > (this.EXPIRATION_DAYS * this.MS_PER_DAY);
  }

  /**
   * Observe customer login and handle wishlist migration
   */
  ensureSessionValidation() {
    // Check on page load
    this.checkCustomerStatus();

    // Listen for customer event
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.account_logout_url) {
      // Customer exists (logout URL is present)
      this.migrateGuestWishlist();
    }
  }

  /**
   * Setup observer for customer login
   */
  setupLoginObserver() {
    // Listen for customer login via form submission
    document.addEventListener('submit', (e) => {
      if (e.target.closest('form[action*="login"]') || e.target.closest('form[action*="account"]')) {
        // Give a moment for login to complete, then trigger migration
        setTimeout(() => this.checkCustomerStatus(), 1000);
      }
    });

    // Listen for navigation events to detect login
    window.addEventListener('load', () => {
      this.checkCustomerStatus();
    });

    // Fallback: Check periodically
    setInterval(() => this.checkCustomerStatus(), 30000); // Every 30 seconds
  }

  /**
   * Check if customer is logged in
   */
  checkCustomerStatus() {
    if (window.Shopify && window.Shopify.checkout && window.Shopify.checkout.customer) {
      this.migrateGuestWishlist();
      return;
    }

    if (window.__st && window.__st.cid) {
      this.migrateGuestWishlist();
      return;
    }

    if (window.Liquid && window.Liquid.customer) {
      this.migrateGuestWishlist();
    }
  }

  /**
   * Migrate guest wishlists to customer account
   * In production, this would sync with your backend
   */
  migrateGuestWishlist() {
    const wishlists = this.getAllWishlists();
    if (wishlists.length === 0) return;

    // Mark as migrated
    wishlists.forEach(w => {
      w.isGuest = false;
      w.migratedAt = new Date().toISOString();
    });

    this.saveWishlists(wishlists);
    this.broadcastChange('wishlist_migrated', { count: wishlists.length });

    // In a real scenario, you'd send this to your backend here
    // await this.syncWithServer(wishlists);
  }

  /**
   * Broadcast changes to other tabs/windows
   * @param {string} eventType - Type of change
   * @param {Object} data - Data associated with the change
   */
  broadcastChange(eventType, data) {
    const event = new CustomEvent('guestWishlistChange', {
      detail: { eventType, data, timestamp: new Date().toISOString() }
    });
    window.dispatchEvent(event);

    // Also broadcast via StorageEvent for cross-tab communication
    if (window.BroadcastChannel) {
      try {
        const channel = new BroadcastChannel('guest_wishlist');
        channel.postMessage({ eventType, data });
        channel.close();
      } catch (error) {
        // BroadcastChannel not supported, fallback to custom events
      }
    }
  }

  /**
   * Listen for wishlist changes
   * @param {Function} callback - Function to call when changes occur
   * @returns {Function} Unsubscribe function
   */
  onWishlistChange(callback) {
    const handler = (e) => callback(e.detail);
    window.addEventListener('guestWishlistChange', handler);

    return () => {
      window.removeEventListener('guestWishlistChange', handler);
    };
  }

  /**
   * Get wishlist statistics
   * @returns {Object} Stats including total wishlists and items
   */
  getStats() {
    const wishlists = this.getAllWishlists();
    const totalItems = wishlists.reduce((sum, w) => sum + w.items.length, 0);

    return {
      totalWishlists: wishlists.length,
      totalItems,
      averageItemsPerList: wishlists.length > 0 ? (totalItems / wishlists.length).toFixed(1) : 0,
      oldestList: wishlists.length > 0 ? wishlists.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0] : null
    };
  }
}

// Initialize globally
window.guestWishlistManager = new GuestWishlistManager();
window.dispatchEvent(new CustomEvent('guestWishlistManagerReady'));
